import React, { useState, useEffect, useRef } from 'react';
import { Lock, User, AlertCircle, ShieldCheck, KeyRound, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import logo from '../assets/logo.svg';

import { versionLine } from "../lib/appVersion";
// ─── Portal Login Mode ──────────────────────────────────────────────────────

function PortalLoginMode({ onPortalLogin, onBack }) {
  const [accessCode, setAccessCode] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // P113: the PATIENT session length, read from the server before sign-in.
  // ⚠ This is the patient value and has nothing to do with the staff timeout —
  // separate constant, separate module, separate endpoint, by design.
  const [portalPolicy, setPortalPolicy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await window.zignagenixAPI?.portalPolicy?.();
        if (!cancelled && p && !p.error && typeof p.sessionTtlMinutes === 'number') {
          setPortalPolicy(p);
        }
      } catch {
        // Unreachable policy: print no number rather than a wrong one.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!accessCode.trim() || !pin.trim()) {
      setError('Please enter both your access code and PIN.');
      return;
    }
    setLoading(true);
    try {
      const result = await window.zignagenixAPI.portalLogin(
        accessCode.trim().toUpperCase(),
        pin.trim()
      );
      if (result?.success) {
        onPortalLogin(result);
      } else {
        setError(result?.message || 'Invalid access code or PIN.');
      }
    } catch (err) {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page" style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)' }}>
      <div className="login-card">
        <div className="login-header">
          <img src={logo} alt="ZignaGenix" className="login-logo" />
          <h1 className="login-title">Patient Portal</h1>
          <p className="login-subtitle">Secure access to your health records</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit} autoComplete="off">
          {error && (
            <div className="login-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Instructions */}
          <div style={{
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: 8,
            padding: '0.75rem 1rem',
            marginBottom: '0.5rem',
            fontSize: '0.8rem',
            color: '#166534',
            lineHeight: 1.5,
          }}>
            <strong>Access your health records securely.</strong> Use the access code
            and PIN provided by your healthcare provider.
          </div>

          <div className="login-field">
            <label className="login-label" htmlFor="portal-access-code">
              <KeyRound size={14} /> Access Code
            </label>
            <input
              id="portal-access-code"
              type="text"
              className="login-input"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
              placeholder="Enter your 12-character access code"
              autoFocus
              autoComplete="off"
              disabled={loading}
              maxLength={12}
              style={{ letterSpacing: '0.15em', fontFamily: 'monospace' }}
            />
          </div>

          <div className="login-field">
            <label className="login-label" htmlFor="portal-pin">
              <Lock size={14} /> PIN
            </label>
            <input
              id="portal-pin"
              type="password"
              className="login-input"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter your PIN"
              autoComplete="off"
              disabled={loading}
              // H3 (audit Apr 26 2026): widened from 4 → 8. Old 4-digit
              // PINs still authenticate; new patients get 8-digit PINs.
              maxLength={8}
              inputMode="numeric"
              style={{ letterSpacing: '0.3em', textAlign: 'center', fontSize: 18 }}
            />
          </div>

          <button type="submit" className="login-button" disabled={loading}
            style={{ background: loading ? '#94a3b8' : 'linear-gradient(135deg, #2563eb, #1d4ed8)' }}
          >
            {loading ? 'Verifying...' : 'Access Portal'}
          </button>

          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <button
              type="button"
              onClick={onBack}
              style={{
                background: 'none', border: 'none', color: '#2E8B98', cursor: 'pointer',
                fontSize: 13, padding: 4, display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >
              <ArrowLeft size={14} /> Back to Staff Login
            </button>
          </div>
        </form>

        <div className="login-footer">
          <p className="login-hipaa-notice">
            <Lock size={12} /> HIPAA-compliant access. All activity is logged and audited.
          </p>
          {/* P113 — THE LABEL NOW DESCRIBES WHAT ACTUALLY HAPPENS.
              It read "Session timeout: 15 minutes" as a literal. Both halves
              were wrong: the server enforces 30 minutes, and it is an ABSOLUTE
              cap from sign-in, not an inactivity timeout — nothing extends it,
              so "timeout" invited exactly the wrong expectation. A patient
              idle 29 minutes stays in; one reading at minute 31 is cut off.
              The number comes off the wire; with no policy we print none. */}
          <p className="login-version">
            {versionLine()}{portalPolicy ? ` | Sessions end ${portalPolicy.sessionTtlMinutes} minutes after sign-in` : ''}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Main Login Page ─────────────────────────────────────────────────────────

function LoginPage({ onLogin, onRecovery, onPortalLogin, sessionExpired }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [portalMode, setPortalMode] = useState(false);
  const [policyMinutes, setPolicyMinutes] = useState(null);
  const usernameRef = useRef(null);

  // P110 (f): the STAFF idle timeout, read from the server before login.
  // ⚠ Staff only — the patient-portal panel below keeps its own footer and is
  // out of scope for this card.
  useEffect(() => {
    const off = window.zignagenixAPI?.onSessionPolicy?.(
      (p) => setPolicyMinutes(p?.idleTimeoutMinutes ?? null)
    );
    return () => { if (typeof off === 'function') off(); };
  }, []);
  const passwordRef = useRef(null);

  // TOTP verification step (shown after successful password auth for admin/physician)
  const [totpStep, setTotpStep] = useState(false);
  const [totpToken, setTotpToken] = useState('');
  const [totpChallengeId, setTotpChallengeId] = useState(null);

  // ── Kill browser autofill ────────────────────────────────────────────────
  // Browsers ignore autocomplete="off" on login forms. We forcibly clear
  // the fields after a short delay (gives the browser time to autofill,
  // then we wipe it). Also clear the native input values directly in case
  // React state and DOM diverge from autofill.
  useEffect(() => {
    const timer = setTimeout(() => {
      setUsername('');
      setPassword('');
      if (usernameRef.current) usernameRef.current.value = '';
      if (passwordRef.current) passwordRef.current.value = '';
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Human-readable login message — strips any error codes / stack-trace bits
  // so the user never sees raw API output. Maps known error shapes to friendly
  // copy; falls back to a generic sign-in-failed line.
  function _friendlyLoginMessage(result) {
    if (!result) return 'Sign-in failed. Please try again.';
    const msg = (result.message || '').trim();
    const code = (result.code || '').toUpperCase();
    // Strip anything that looks like an error code in brackets/parens
    const clean = msg.replace(/\s*\[?[A-Z_]{4,}\]?\s*/g, ' ').replace(/\s{2,}/g, ' ').trim();
    if (code === 'TRAINING_OVERDUE_BLOCKED') {
      return 'Your required compliance training is overdue. Sign-in is blocked — please contact your administrator to record your completed training.';
    }
    if (code === 'INVALID_CREDENTIALS' || /invalid|incorrect|wrong/i.test(msg)) {
      return 'The username or password you entered is incorrect.';
    }
    if (code === 'ACCOUNT_LOCKED' || /lock/i.test(msg)) {
      return 'Your account is temporarily locked. Please try again in a few minutes or contact your administrator.';
    }
    if (code === 'ACCOUNT_INACTIVE' || /inactive|disabled/i.test(msg)) {
      return 'Your account is inactive. Please contact your administrator.';
    }
    if (code === 'RATE_LIMITED' || /rate.?limit|too many/i.test(msg)) {
      return 'Too many sign-in attempts. Please wait a moment and try again.';
    }
    if (code === 'PASSWORD_EXPIRED') {
      return 'Your password has expired. You will be prompted to set a new one.';
    }
    return clean || 'Sign-in failed. Please try again.';
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password.trim()) {
      setError('Please enter both your username and password.');
      return;
    }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.login(username, password);
      // Always clear password field and re-mask after attempt
      setPassword('');
      setShowPassword(false);
      if (result?.error) {
        setError(_friendlyLoginMessage(result));
      } else if (result?.requiresTOTPVerification) {
        // 2FA-enabled account — no session was minted. Stash the single-use
        // challenge id and show the verification step.
        setTotpChallengeId(result.challengeId || null);
        setTotpToken('');
        setTotpStep(true);
      } else if (result?.success) {
        onLogin(result.user, result);
      } else {
        // Neither success nor a recognized error → don't leave the user
        // staring at a blank loading screen.
        setError('Sign-in failed. Please try again.');
      }
    } catch (err) {
      setPassword('');
      setShowPassword(false);
      setError('Sign-in failed. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleTotpSubmit(e) {
    e.preventDefault();
    setError('');
    const token = totpToken.replace(/\s/g, '');
    if (!token) {
      setError('Please enter your 6-digit verification code');
      return;
    }
    setLoading(true);
    try {
      const result = await window.zignagenixAPI.completeTotpLogin(totpChallengeId, token);
      if (result.error) {
        // The challenge expired, was already consumed, or the account became
        // unavailable — send the user back to the password step to restart.
        if (result.code === 'NO_PENDING_LOGIN' || result.code === 'ACCOUNT_UNAVAILABLE') {
          setTotpStep(false);
          setTotpToken('');
          setTotpChallengeId(null);
          setPassword('');
          setError(result.message || 'Your sign-in expired. Please sign in again.');
        } else {
          setTotpToken('');
          setError(result.message || 'Invalid verification code.');
        }
      } else if (result.success) {
        setTotpChallengeId(null);
        onLogin(result.user, result);
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // Portal login mode — separate view
  if (portalMode) {
    return (
      <PortalLoginMode
        onPortalLogin={onPortalLogin}
        onBack={() => setPortalMode(false)}
      />
    );
  }

  // TOTP verification screen
  if (totpStep) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-header">
            <img src={logo} alt="ZignaGenix" className="login-logo" />
            <h1 className="login-title">Two-Factor Authentication</h1>
            <p className="login-subtitle" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <ShieldCheck size={16} /> Enter your authenticator code
            </p>
          </div>

          <form className="login-form" onSubmit={handleTotpSubmit}>
            {error && (
              <div className="login-error">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            <div className="login-field">
              <label className="login-label" htmlFor="totp-token">
                <ShieldCheck size={14} /> Verification Code
              </label>
              <input
                id="totp-token"
                type="text"
                inputMode="numeric"
                pattern="[0-9A-Z]{6,8}"
                className="login-input"
                value={totpToken}
                onChange={(e) => setTotpToken(e.target.value)}
                placeholder="000000"
                autoFocus
                autoComplete="one-time-code"
                disabled={loading}
                style={{ letterSpacing: '0.3em', fontSize: 20, textAlign: 'center' }}
              />
              <p style={{ fontSize: 12, color: '#6C757D', marginTop: 6, textAlign: 'center' }}>
                Enter the 6-digit code from your authenticator app, or an 8-character backup code.
              </p>
            </div>

            <button type="submit" className="login-button" disabled={loading}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              {loading && (
                <span aria-hidden="true" style={{
                  width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)',
                  borderTopColor: '#FFFFFF', borderRadius: '50%',
                  display: 'inline-block', animation: 'zg-login-spin 0.7s linear infinite',
                }} />
              )}
              <span>{loading ? 'Verifying your code…' : 'Verify'}</span>
            </button>

            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button
                type="button"
                onClick={() => { setTotpStep(false); setTotpToken(''); setError(''); }}
                style={{
                  background: 'none', border: 'none', color: '#2E8B98', cursor: 'pointer',
                  fontSize: 13, textDecoration: 'underline', padding: 4
                }}
              >
                ← Back to sign in
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <img src={logo} alt="ZignaGenix" className="login-logo" />
          <h1 className="login-title">ZignaGenix</h1>
          <p className="login-subtitle">Medical Intelligence Platform</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit} autoComplete="off">
          {sessionExpired && !error && (
            <div className="login-error" style={{ background: '#fff3e0', borderColor: '#f9a825', color: '#e65100' }}>
              <AlertCircle size={16} />
              {/* P110: an automatic logoff, a takeover and a generic expiry are
                  three different events and the person in front of the screen
                  needs to know which one happened. `displaced` was already
                  written here but nothing ever set it — App.js now does, off
                  the cross-tab takeover signal. */}
              {/* ⛔ P130: "Your session has expired" was the DEFAULT, and on
                  26 Oct it was simply false — the server had logged no expiry
                  at all and the session had been ended somewhere else. The
                  default now says what is actually known, which is less tidy
                  and more useful: it stops someone hunting for a timeout
                  setting that was never involved. */}
              <span>{
                sessionExpired === 'displaced'
                  ? 'You were signed out because your account signed in somewhere else.'
                  : sessionExpired === 'idle_timeout'
                    ? `Signed out after ${policyMinutes || 30} minutes of inactivity.`
                    : sessionExpired === 'ended_elsewhere'
                      ? 'Your session was ended somewhere else — another tab, another device, or an administrator. It was not an inactivity timeout.'
                      : 'Your session is no longer active. Please sign in again.'
              }</span>
            </div>
          )}
          {error && (
            <div className="login-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <div className="login-field">
            <label className="login-label" htmlFor="zg-user-field">
              <User size={14} /> Username
            </label>
            <input
              ref={usernameRef}
              id="zg-user-field"
              name="zg_user_nocache"
              type="text"
              className="login-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              autoFocus
              autoComplete="one-time-code"
              disabled={loading}
            />
          </div>

          <div className="login-field">
            <label className="login-label" htmlFor="zg-pass-field">
              <Lock size={14} /> Password
            </label>
            <div className="login-password-wrapper">
              <input
                ref={passwordRef}
                id="zg-pass-field"
                name="zg_pass_nocache"
                type={showPassword ? 'text' : 'password'}
                className="login-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="one-time-code"
                disabled={loading}
              />
              <button type="button" className="login-password-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button type="submit" className="login-button" disabled={loading}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {loading && (
              <span className="zg-login-spinner" aria-hidden="true" style={{
                width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)',
                borderTopColor: '#FFFFFF', borderRadius: '50%',
                display: 'inline-block', animation: 'zg-login-spin 0.7s linear infinite',
              }} />
            )}
            <span>{loading ? 'Signing you in securely…' : 'Sign In'}</span>
          </button>
          <style>{`@keyframes zg-login-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

          {onRecovery && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button
                type="button"
                onClick={onRecovery}
                style={{
                  background: 'none', border: 'none', color: '#2E8B98', cursor: 'pointer',
                  fontSize: 13, textDecoration: 'underline', padding: 4
                }}
              >
                Forgot username or password?
              </button>
            </div>
          )}
        </form>

        {/* Divider */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          margin: '1rem 1.5rem 0.75rem',
        }}>
          <div style={{ flex: 1, height: 1, background: '#dee2e6' }} />
          <span style={{ fontSize: 12, color: '#6C757D', fontWeight: 500 }}>OR</span>
          <div style={{ flex: 1, height: 1, background: '#dee2e6' }} />
        </div>

        {/* Patient Portal Button */}
        <div style={{ padding: '0 1.5rem 0.5rem' }}>
          <button
            type="button"
            onClick={() => setPortalMode(true)}
            style={{
              width: '100%',
              padding: '0.7rem 1rem',
              border: '2px solid #2563eb',
              borderRadius: 8,
              background: '#eff6ff',
              color: '#1d4ed8',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => { e.target.style.background = '#dbeafe'; }}
            onMouseLeave={(e) => { e.target.style.background = '#eff6ff'; }}
          >
            <KeyRound size={16} /> Patient Portal Access
          </button>
        </div>

        <div className="login-footer">
          <p className="login-hipaa-notice">
            <Lock size={12} /> HIPAA-compliant access. All activity is logged and audited.
          </p>
          {/* P110 (f): the number comes off the wire (GET /api/session-policy),
              never from a literal here. A sign-in page that advertises a
              timeout the server does not enforce is how staff learn to
              distrust the warning dialog. Until the policy lands we print the
              label without a number rather than a stale one. */}
          <p className="login-version">
            {versionLine()}{policyMinutes ? ` | Session timeout: ${policyMinutes} minutes` : ''}
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
